import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';

import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  onIdTokenChanged,
} from "firebase/auth";

import firebaseAuth from "./config/firebaseConfig";
import { setAuthToken } from "./services/api";
import { getAdminsList } from "./services/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [unauthorizedError, setUnauthorizedError] = useState(null);
  const refreshTimeoutRef = useRef(null);

  // Check if user is an admin. Returns { isAdmin } on success; throws on API/network failure so caller can avoid signing out on transient errors.
  const checkAdminStatus = async (userEmail) => {
    const response = await getAdminsList();
    const adminEmails = response.data || [];
    return adminEmails.includes(userEmail);
  };

  // Proactive token refresh - refresh 5 minutes before expiration
  const scheduleTokenRefresh = useCallback((user) => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    if (user) {
      // Firebase tokens typically expire in 1 hour, refresh at 55 minutes
      const refreshTime = 55 * 60 * 1000; // 55 minutes in milliseconds
      
      refreshTimeoutRef.current = setTimeout(async () => {
        try {
          const token = await user.getIdToken(true); // Force refresh
          setAuthToken(token);
          console.log('Token refreshed proactively');
          scheduleTokenRefresh(user); // Schedule next refresh
        } catch (error) {
          console.error('Error refreshing token:', error);
        }
      }, refreshTime);
    }
  }, []);

  useEffect(() => {
    const unsubscribeAuthState = onAuthStateChanged(firebaseAuth, async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          setAuthToken(token);
          const isAdmin = await checkAdminStatus(user.email);
          if (!isAdmin) {
            setUnauthorizedError('Unauthorized: You do not have admin access to this application.');
            await firebaseSignOut(firebaseAuth);
            setAuthToken(null);
            setIsSignedIn(false);
            setUser(null);
            if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
            setIsLoading(false);
            return;
          }
          setUnauthorizedError(null);
          setIsSignedIn(true);
          setUser(user);
          scheduleTokenRefresh(user);
        } catch (error) {
          // API/network failure: don't sign out (avoids logout on refresh). Keep user in.
          console.error('Error during auth check:', error);
          setUnauthorizedError(null);
          setIsSignedIn(true);
          setUser(user);
          scheduleTokenRefresh(user);
        }
        setIsLoading(false);
      } else {
        setIsSignedIn(false);
        setUser(null);
        setAuthToken(null);
        setUnauthorizedError(null);
        if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
        setIsLoading(false);
      }
    });

    // Set up token refresh listener for automatic refresh
    const unsubscribeIdToken = onIdTokenChanged(firebaseAuth, async (user) => {
      if (user) {
        const token = await user.getIdToken();
        setAuthToken(token);
        scheduleTokenRefresh(user);
      } else {
        setAuthToken(null);
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
        }
      }
    });

    // Cleanup listeners and timeout on unmount
    return () => {
      unsubscribeAuthState();
      unsubscribeIdToken();
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [scheduleTokenRefresh]);

  const signIn = async (email, password) => {
    try {
      setIsLoading(true);
      setUnauthorizedError(null);
      const userCredential = await signInWithEmailAndPassword(
        firebaseAuth,
        email,
        password
      );
      const idToken = await userCredential.user.getIdToken();
      setAuthToken(idToken);
      
      // Check if user is an admin
      const isAdmin = await checkAdminStatus(email);
      if (!isAdmin) {
        // User is not an admin, sign them out
        await firebaseSignOut(firebaseAuth);
        setAuthToken(null);
        setUnauthorizedError('Unauthorized: You do not have admin access to this application.');
        return 'Unauthorized: You do not have admin access to this application.';
      }
      
      console.info("User signed in successfully:", userCredential.user);
      return null; // No error
    } catch (err) {
      console.error(err.message);
      return err.message;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      await firebaseSignOut(firebaseAuth);
      setAuthToken(null);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      console.info("User signed out successfully");
    } catch (err) {
      console.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to get fresh token (useful for API calls)
  const getFreshToken = async () => {
    if (user) {
      try {
        return await user.getIdToken(true);
      } catch (error) {
        console.error('Error getting fresh token:', error);
        return null;
      }
    }
    return null;
  };

  return (
    <AuthContext.Provider
      value={{
        isSignedIn,
        user,
        isLoading,
        signIn,
        signOut,
        getFreshToken,
        unauthorizedError,
        setUnauthorizedError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);